import { useEffect, useMemo, useState } from 'react';
import {
  Accessibility,
  AlignLeft,
  Check,
  Contrast,
  Droplets,
  ImageOff,
  Link2,
  Pause,
  RefreshCcw,
  Type,
  X,
} from 'lucide-react';

const STORAGE_KEY = 'stratel-a11y-preferences';

const DEFAULT_PREFERENCES = {
  contrastMode: 'default',
  textSize: 'default',
  highlightLinks: false,
  textSpacing: false,
  pauseAnimations: false,
  hideImages: false,
  readableFont: false,
  lineHeight: 'default',
  textAlign: 'default',
  grayscale: false,
};

function readStoredPreferences() {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCES;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_PREFERENCES, ...parsed };
  } catch (_error) {
    return DEFAULT_PREFERENCES;
  }
}

function applyPreferences(preferences) {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  root.dataset.a11yContrast = preferences.contrastMode;
  root.dataset.a11yTextSize = preferences.textSize;
  root.dataset.a11yHighlightLinks = preferences.highlightLinks ? 'on' : 'off';
  root.dataset.a11yTextSpacing = preferences.textSpacing ? 'expanded' : 'default';
  root.dataset.a11yReducedMotion = preferences.pauseAnimations ? 'on' : 'off';
  root.dataset.a11yHideImages = preferences.hideImages ? 'on' : 'off';
  root.dataset.a11yReadableFont = preferences.readableFont ? 'on' : 'off';
  root.dataset.a11yLineHeight = preferences.lineHeight;
  root.dataset.a11yTextAlign = preferences.textAlign;
  root.dataset.a11yGrayscale = preferences.grayscale ? 'on' : 'off';
}

function PreferenceButton({ active, icon: Icon, label, onClick, value }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex min-h-[72px] flex-col items-center justify-center gap-1.5 rounded-xl border px-2 py-3 text-center transition sm:min-h-[88px] sm:gap-2 sm:rounded-2xl sm:px-3 sm:py-4 ${
        active
          ? 'border-primary-600 bg-primary-50 text-primary-900 shadow-sm'
          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
      }`}
    >
      <Icon size={18} aria-hidden="true" />
      <span className="text-xs font-semibold leading-tight">{label}</span>
      {value ? <span className="text-[11px] text-slate-500">{value}</span> : null}
    </button>
  );
}

export default function AccessibilityWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  useEffect(() => {
    const stored = readStoredPreferences();
    setPreferences(stored);
    applyPreferences(stored);
  }, []);

  useEffect(() => {
    applyPreferences(preferences);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    }
  }, [preferences]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  const activeCount = useMemo(
    () =>
      Object.entries(preferences).reduce((count, [key, value]) => {
        if (key === 'contrastMode') return count + (value !== 'default' ? 1 : 0);
        if (key === 'textSize') return count + (value !== 'default' ? 1 : 0);
        if (key === 'lineHeight') return count + (value !== 'default' ? 1 : 0);
        if (key === 'textAlign') return count + (value !== 'default' ? 1 : 0);
        return count + (value ? 1 : 0);
      }, 0),
    [preferences],
  );

  const cycleTextSize = () => {
    setPreferences((current) => ({
      ...current,
      textSize:
        current.textSize === 'default'
          ? 'large'
          : current.textSize === 'large'
            ? 'xlarge'
            : 'default',
    }));
  };

  const cycleContrastMode = () => {
    setPreferences((current) => ({
      ...current,
      contrastMode:
        current.contrastMode === 'default'
          ? 'invert'
          : current.contrastMode === 'invert'
            ? 'dark'
            : current.contrastMode === 'dark'
              ? 'light'
              : 'default',
    }));
  };

  const cycleLineHeight = () => {
    setPreferences((current) => ({
      ...current,
      lineHeight: current.lineHeight === 'default' ? 'relaxed' : 'default',
    }));
  };

  const cycleTextAlign = () => {
    setPreferences((current) => ({
      ...current,
      textAlign: current.textAlign === 'default' ? 'left' : 'default',
    }));
  };

  const resetPreferences = () => {
    setPreferences(DEFAULT_PREFERENCES);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
        aria-controls="accessibility-widget-panel"
        aria-label="Open accessibility settings"
        className="fixed bottom-3 right-3 z-[120] inline-flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-white bg-primary-700 text-white shadow-xl transition hover:scale-[1.03] hover:bg-primary-800 focus:outline-none focus:ring-4 focus:ring-primary-300 sm:bottom-5 sm:right-5 sm:h-14 sm:w-14 sm:border-4"
      >
        <Accessibility size={24} aria-hidden="true" />
        {activeCount > 0 ? (
          <span className="absolute -right-1 -top-1 inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-green-600 text-white shadow-md">
            <Check size={14} aria-hidden="true" />
          </span>
        ) : null}
      </button>

      {isOpen ? (
        <div
          className="fixed inset-0 z-[119] bg-slate-950/30"
          aria-hidden="true"
          onClick={() => setIsOpen(false)}
        />
      ) : null}

      <aside
        id="accessibility-widget-panel"
        role="dialog"
        aria-modal="false"
        aria-label="Accessibility settings"
        className={`fixed bottom-24 left-3 right-3 z-[121] max-h-[calc(100dvh-8rem)] overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-3 shadow-2xl transition-all duration-200 sm:left-auto sm:right-4 sm:w-[calc(100vw-2rem)] sm:max-w-sm sm:rounded-3xl sm:p-4 ${
          isOpen ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-4 opacity-0'
        }`}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Accessibility Menu</p>
            <p className="text-xs text-slate-500">
              {activeCount > 0 ? `${activeCount} preference${activeCount === 1 ? '' : 's'} active` : 'No preferences enabled'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
            aria-label="Close accessibility settings"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          <PreferenceButton
            active={preferences.contrastMode !== 'default'}
            icon={Contrast}
            label="Contrast"
            value={
              preferences.contrastMode === 'invert'
                ? 'Invert'
                : preferences.contrastMode === 'dark'
                  ? 'Dark'
                  : preferences.contrastMode === 'light'
                    ? 'Light'
                    : null
            }
            onClick={cycleContrastMode}
          />
          <PreferenceButton
            active={preferences.highlightLinks}
            icon={Link2}
            label="Highlight Links"
            onClick={() => setPreferences((current) => ({ ...current, highlightLinks: !current.highlightLinks }))}
          />
          <PreferenceButton
            active={preferences.textSize !== 'default'}
            icon={Type}
            label="Bigger Text"
            value={preferences.textSize === 'large' ? 'Large' : preferences.textSize === 'xlarge' ? 'XL' : null}
            onClick={cycleTextSize}
          />
          <PreferenceButton
            active={preferences.textSpacing}
            icon={Type}
            label="Text Spacing"
            onClick={() => setPreferences((current) => ({ ...current, textSpacing: !current.textSpacing }))}
          />
          <PreferenceButton
            active={preferences.pauseAnimations}
            icon={Pause}
            label="Pause Animations"
            onClick={() => setPreferences((current) => ({ ...current, pauseAnimations: !current.pauseAnimations }))}
          />
          <PreferenceButton
            active={preferences.hideImages}
            icon={ImageOff}
            label="Hide Images"
            onClick={() => setPreferences((current) => ({ ...current, hideImages: !current.hideImages }))}
          />
          <PreferenceButton
            active={preferences.readableFont}
            icon={Type}
            label="Dyslexia Friendly"
            onClick={() => setPreferences((current) => ({ ...current, readableFont: !current.readableFont }))}
          />
          <PreferenceButton
            active={preferences.lineHeight !== 'default'}
            icon={Type}
            label="Line Height"
            value={preferences.lineHeight === 'relaxed' ? 'Relaxed' : null}
            onClick={cycleLineHeight}
          />
          <PreferenceButton
            active={preferences.textAlign !== 'default'}
            icon={AlignLeft}
            label="Text Align"
            value={preferences.textAlign === 'left' ? 'Left' : null}
            onClick={cycleTextAlign}
          />
          <PreferenceButton
            active={preferences.grayscale}
            icon={Droplets}
            label="Reduce Saturation"
            onClick={() => setPreferences((current) => ({ ...current, grayscale: !current.grayscale }))}
          />
        </div>

        <button
          type="button"
          onClick={resetPreferences}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-secondary-900 px-4 py-3 text-sm font-semibold text-white hover:bg-secondary-800"
        >
          <RefreshCcw size={16} aria-hidden="true" />
          Reset Accessibility Settings
        </button>
      </aside>
    </>
  );
}
